import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Crown } from "lucide-react";
import Nav from "@/components/Nav";
import FloatingShapes from "@/components/FloatingShapes";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Footer from "@/components/Footer";
import PayPalCheckout from "@/components/PayPalCheckout";
import { capturePaymentSuccess } from "@/lib/analytics";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const CURRENCY_LABELS = {
  INR: "Indian Rupee", USD: "US Dollar", EUR: "Euro", GBP: "British Pound",
  CAD: "Canadian Dollar", AUD: "Australian Dollar", AED: "UAE Dirham",
  SGD: "Singapore Dollar", JPY: "Japanese Yen", MXN: "Mexican Peso",
  SAR: "Saudi Riyal", BRL: "Brazilian Real", NGN: "Nigerian Naira",
};

// Lazy-load the Razorpay checkout script and resolve once window.Razorpay is ready.
function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("script-load-failed")));
      if (window.Razorpay) resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SRC; s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("script-load-failed"));
    document.body.appendChild(s);
  });
}

// Fire-and-forget checkout analytics so drop-off is visible in the admin dashboard.
function track(kind, extra = {}) {
  try { console.log(`[checkout] ${kind}`, extra); } catch {}
  api.post("/events/paywall", { feature: "checkout", kind, ...extra }).catch(() => {});
}

const TIERS = [
  { id: "free", name: "Free", price: "₹0", per: "", color: "#E7E5E4", isFree: true,
    bullets: [
      "5 deck generations (first 4 full access)",
      "PDFs up to 50 pages",
      "2 of 10 quiz questions (preview)",
      "No PDF / PPT exports",
      "5th deck: 50% sections preview",
    ]},
  { id: "pro_monthly", name: "Pro Monthly", price: "₹499", per: "/mo", color: "#DDD6FE",
    bullets: [
      "Unlimited deck generations",
      "PDFs up to 200 pages",
      "Full 10-question quizzes",
      "2 fresh question sets per section",
      "PDF & PPT exports",
      "No ads",
    ]},
  { id: "pro_annual", name: "Pro Annual", price: "₹3,499", per: "/yr", color: "#A7F3D0", featured: true,
    bullets: [
      "Everything in Pro Monthly",
      "Save 42% vs monthly",
      "PDFs up to 200 pages",
      "PDF & PPT exports",
      "Best for serious learners",
    ]},
  { id: "lifetime", name: "Lifetime", price: "₹4,999", per: " one-time", color: "#FEF08A",
    bullets: [
      "Everything in Pro, forever",
      "PDFs up to 1,000 pages",
      "5 fresh question sets per section",
      "Inner Circle member badge",
      "Invite 5 friends to your library",
      "Locked-in price",
    ]},
];

export default function Pricing() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [geo, setGeo] = useState(null);
  const [currency, setCurrency] = useState(null); // null = let backend auto-detect
  const [promo, setPromo] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const applyPromo = async () => {
    if (!promo.trim()) { toast.error("Enter a promo code first."); return; }
    if (!user) { nav("/login"); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: promo.trim() });
      await checkAuth();
      toast.success(data.message || "Promo applied! ✨");
      setPromo("");
      setTimeout(() => nav("/dashboard"), 1400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply promo code.");
    } finally {
      setRedeeming(false);
    }
  };

  const loadPricing = useCallback(async (cur) => {
    try {
      const q = cur ? `?currency=${cur}` : "";
      const { data } = await api.get(`/pricing${q}`);
      setGeo(data);
      setCurrency(data.currency);
    } catch (e) { console.error("[pricing] failed to load regional pricing", e); }
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  // Local price for a plan (falls back to the static INR label before geo loads).
  const priceFor = (t) => {
    if (t.isFree) return `${geo?.symbol || "₹"}0`;
    const p = geo?.plans?.[t.id];
    if (!p) return t.price;
    return `${geo.symbol}${Number(p.local).toLocaleString()}`;
  };
  const isINR = !geo || geo.currency === "INR";

  const buy = async (planId) => {
    track("buy_click", { plan: planId });
    if (!user) { nav("/login"); return; }

    // Lazy-load the Razorpay script and WAIT for it (no hard stop if not yet ready).
    try {
      await loadRazorpay();
    } catch (e) {
      console.error("[checkout] Razorpay script failed to load", e);
      track("script_load_error", { plan: planId });
      toast.error("Couldn't load the payment window. Check your connection and try again.");
      return;
    }

    let order;
    try {
      order = (await api.post("/checkout/order", { plan: planId })).data;
      track("order_created", { plan: planId });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not start checkout";
      console.error("[checkout] order creation failed", { plan: planId, status: e?.response?.status, detail: msg });
      track("order_error", { plan: planId });
      toast.error(msg);
      return;
    }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency,
      name: "Loom", description: TIERS.find((t) => t.id === planId)?.name || "Loom plan",
      order_id: order.order_id,
      prefill: { name: order.name, email: order.email, contact: order.contact || "" },
      theme: { color: "#6366f1" },
      handler: async (resp) => {
        const t = toast.loading("Verifying payment…");
        try {
          await api.post("/checkout/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          track("payment_verified", { plan: planId });
          capturePaymentSuccess(user, planId, resp.razorpay_payment_id);
          await checkAuth();
          toast.success("Payment successful! ✨", { id: t });
          setTimeout(() => nav("/dashboard"), 1200);
        } catch (e) {
          console.error("[checkout] verification failed", e?.response?.data);
          toast.error(e?.response?.data?.detail || "Verification failed", { id: t });
        }
      },
      modal: { ondismiss: () => { track("checkout_dismissed", { plan: planId }); toast("Checkout cancelled"); } },
    });
    rzp.on("payment.failed", (resp) => {
      console.error("[checkout] payment.failed", resp?.error);
      track("payment_failed", { plan: planId });
      toast.error(resp?.error?.description || "Payment failed");
    });
    track("checkout_opened", { plan: planId });
    rzp.open();
  };

  return (
    <div className="relative min-h-screen grain" data-testid="pricing-page">
      <FloatingShapes /><Nav />
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center">
          <span className="tag-pill">unlock the loom</span>
          <h1 className="font-display text-5xl sm:text-6xl mt-5">Pick a pocket of paper</h1>
          <p className="font-hand text-stone-500 text-2xl mt-2">cancel anytime · no card games</p>
          <p className="text-xs text-stone-400 mt-3" data-testid="payment-method-note">
            {isINR
              ? "Pay with UPI, cards & netbanking via Razorpay, or with PayPal"
              : "Pay securely with PayPal — no Razorpay required"}
          </p>
          {geo && (
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-stone-600" data-testid="currency-switcher-wrap">
              <span className="text-stone-400">Prices for your region:</span>
              <select
                value={currency || geo.currency}
                onChange={(e) => { setCurrency(e.target.value); loadPricing(e.target.value); }}
                className="border border-stone-200 rounded-full px-3 py-1.5 bg-white text-stone-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 font-medium"
                data-testid="currency-select"
              >
                {geo.supported_currencies.map((c) => <option key={c} value={c}>{c} — {CURRENCY_LABELS[c] || c}</option>)}
              </select>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-2" data-testid="promo-section">
            <div className="flex items-center gap-2">
              <input
                value={promo}
                onChange={(e) => setPromo(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                placeholder="Have a promo code?"
                className="border border-stone-200 rounded-full px-4 py-2 text-sm bg-white text-stone-800 w-48 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="promo-input"
              />
              <button
                onClick={applyPromo}
                disabled={redeeming}
                className="btn-ghost text-sm border border-stone-200 disabled:opacity-60"
                data-testid="promo-apply-button"
              >
                {redeeming ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {TIERS.map((t, i) => (
            <div key={t.id} className="sticky-card p-8 relative overflow-hidden"
                 style={{ background: `linear-gradient(180deg, ${t.color}66 0%, #fff 60%)`,
                          transform: `rotate(${(i-1.5)*0.4}deg)` }}
                 data-testid={`plan-card-${t.id}`}>
              {t.featured && (
                <div className="absolute top-3 right-3 tag-pill bg-indigo-50 text-indigo-600">most loved</div>
              )}
              <div className="font-display text-3xl flex items-center gap-2">
                {t.id === "lifetime" && <Crown className="w-5 h-5 text-amber-500"/>}
                {t.name}
              </div>
              <div className="mt-3">
                <span className="font-display text-5xl" data-testid={`plan-price-${t.id}`}>{priceFor(t)}</span>
                <span className="text-stone-500">{t.per}</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {t.bullets.map((b, j) => (
                  <li key={j} className="flex gap-2 text-stone-700">
                    <Check className="w-4 h-4 text-indigo-500 mt-1 shrink-0"/>{b}
                  </li>
                ))}
              </ul>
              {t.isFree ? (
                <button onClick={()=>nav(user ? "/dashboard" : "/register")}
                        className="btn-ghost w-full mt-8 inline-flex items-center justify-center gap-2 border border-stone-200"
                        data-testid="buy-free">
                  {user ? "Go to my decks" : "Start free"}
                </button>
              ) : (
                <>
                  {/* Razorpay — India only (UPI + cards + netbanking) */}
                  {isINR && (
                    <button onClick={()=>buy(t.id)}
                            className="btn-primary w-full mt-8 inline-flex items-center justify-center gap-2"
                            data-testid={`buy-${t.id}`}>
                      <Sparkles className="w-4 h-4"/> {user ? "Get " + t.name : "Sign in to buy"}
                    </button>
                  )}
                  {/* PayPal — all countries (primary for non-India, secondary for India) */}
                  <PayPalCheckout
                    plan={t.id} planLabel={t.name}
                    currency={(currency || geo?.currency)}
                    priceDisplay={priceFor(t)}
                    label={isINR ? "or pay with PayPal" : "Pay with PayPal"}
                    primary={!isINR}
                  />
                </>
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-stone-500 mt-12 text-sm">
          Free: 5 decks (first 4 full access, 5th at 50% preview), 2 of 10 quiz questions, no PDF/PPT exports. Upgrade anytime.
        </p>
        {geo && !isINR && (
          <p className="text-center text-stone-400 mt-3 text-xs" data-testid="fx-note">
            Prices shown in {geo.currency} are today's approximate conversion.
            International payments are processed in USD via PayPal; your bank applies its own exchange rate at checkout.
          </p>
        )}
      </main>
      <Footer />
    </div>
  );
}
